import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import {
  BehaviorSubject,
  catchError,
  Observable,
  switchMap,
  take,
  throwError,
  timeout,
  TimeoutError,
} from "rxjs";
import { Crop } from "../../../models/crop.model";
import { Client } from "../../../models/client.model";
import { Url } from "../Url";
import { SnackbarService } from "../../snackbar/snackbar.service";
import { FieldFormData } from "../../../models/field-form.model";
import { AdminData } from "../../../models/admin.model";
import {
  GrowerListResponse,
  UserFieldEtValue,
} from "../../../models/claimed-field.model";
import {
  ApnCountySearchResult,
  FieldFeatureCollection,
} from "../../../models/fields.model";

export interface PreclaimedInfoResponse {
  hasPreclaimedInfo: boolean;
  data: Partial<FieldFormData> | null;
}

export interface ManagerEtDateValuesResponse {
  etDate: string;
  values: Record<string, Omit<UserFieldEtValue, "et_date">>;
}

export interface MonthlyIrrigationInfo {
  irrigated: boolean;
  surface_water: number;
  ground_water: number;
}

export type IrrigatedInfo = Record<string, MonthlyIrrigationInfo>;

export interface ClaimedFieldIrrigatedInfoResponse {
  claimed_field_id: number;
  irrigated_info: IrrigatedInfo;
}

export interface AppliedWaterCalculationPayload {
  claimed_field_id: number;
  crops: Array<{
    crop_number: number;
    crop_type: string;
    start_date: string | Date | null;
    end_date: string | Date | null;
    irrigation_method: string | null;
  }>;
  monthly_irrigated: boolean[];
  monthly_surface_water: number[];
  monthly_ground_water: number[];
  monthly_et: number[];
  monthly_precipitation: number[];
}

export interface AppliedWaterCalculationResponse {
  months: string[];
  surfaceWaterAppliedMed: number[];
  groundWaterAppliedHigh: number[];
  groundWaterAppliedMed: number[];
  groundWaterAppliedLow: number[];
  cumulativeGroundWaterAppliedHigh: number[];
  cumulativeGroundWaterAppliedMed: number[];
  cumulativeGroundWaterAppliedLow: number[];
  cumulativeSurfaceWaterAppliedMed: number[];
}

// ! Need further testing the timeout
@Injectable({
  providedIn: "root",
})
export class DataService {
  private apiUrl = Url;
  private refreshTrigger$ = new BehaviorSubject<void>(undefined);
  private readonly REQUEST_TIMEOUT = 120000; // 30 seconds timeout
  private snackService = inject(SnackbarService);

  // Trigger data refresh across all data endpoints
  triggerRefresh() {
    this.refreshTrigger$.next();
  }

  constructor(private httpClient: HttpClient) {}

  // Helper to apply timeout and retry logic
  private withTimeout<T>(source: Observable<T>): Observable<T> {
    return source.pipe(
      timeout(this.REQUEST_TIMEOUT),
      catchError((error) => {
        if (error instanceof TimeoutError) {
          this.snackService.show({
            message: "Request timed out. Please try again.",
          });
          console.error("Request timed out:", error);
        }
        return throwError(() => error);
      }),
    );
  }

  // Get All Crops from Server
  getAllCrops(): Observable<Crop[]> {
    const url = `${this.apiUrl}/crops`;
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() => this.withTimeout(this.httpClient.get<Crop[]>(url))),
    );
  }

  // Get All Clients from Server
  getAllClients(): Observable<Client[]> {
    const url = `${this.apiUrl}/clients`;
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() => this.withTimeout(this.httpClient.get<Client[]>(url))),
    );
  }

  // Get All Field Boundaries from Server
  getAllFields(
    clientId?: string,
    includeEt = true,
  ): Observable<FieldFeatureCollection> {
    const url = `${this.apiUrl}/fields`;
    let params = new HttpParams();
    if (clientId) {
      params = params.set("clientId", clientId);
    }
    params = params.set("includeEt", String(includeEt));
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() =>
        this.withTimeout(
          this.httpClient.get<FieldFeatureCollection>(url, { params }),
        ),
      ),
    );
  }

  getAllFieldsForDate(
    clientId?: string,
    includeEtDate?: string,
  ): Observable<FieldFeatureCollection> {
    const url = `${this.apiUrl}/fields`;
    let params = new HttpParams();
    if (clientId) {
      params = params.set("clientId", clientId);
    }
    if (includeEtDate) {
      params = params.set("includeEtDate", includeEtDate);
    }
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() =>
        this.withTimeout(
          this.httpClient.get<FieldFeatureCollection>(url, { params }),
        ),
      ),
    );
  }

  getManagerEtValuesForDate(
    etDate: string,
  ): Observable<ManagerEtDateValuesResponse> {
    const url = `${this.apiUrl}/fields/manager-et-date`;
    const params = new HttpParams().set("date", etDate);
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() =>
        this.withTimeout(
          this.httpClient.get<ManagerEtDateValuesResponse>(url, { params }),
        ),
      ),
    );
  }

  // Get list of counties available for APN + county field search
  getCountiesForFieldSearch(selectedDistrictId: number): Observable<string[]> {
    const url = `${this.apiUrl}/fields/search/counties`;
    const params = new HttpParams().set(
      "selected_district_id",
      selectedDistrictId,
    );
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() =>
        this.withTimeout(this.httpClient.get<string[]>(url, { params })),
      ),
    );
  }

  // Search for field uniqueids by APN + county
  searchFieldsByApnAndCounty(
    apn: string,
    county: string,
    selectedDistrictId: number,
  ): Observable<ApnCountySearchResult> {
    const url = `${this.apiUrl}/fields/search`;
    const params = new HttpParams()
      .set("apn", apn)
      .set("county", county)
      .set("selected_district_id", selectedDistrictId);
    return this.withTimeout(
      this.httpClient.get<ApnCountySearchResult>(url, { params }),
    );
  }

  // Get Field Config rows
  getFieldsConfig(): Observable<any[]> {
    const url = `${this.apiUrl}/fields-config`;
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() => this.withTimeout(this.httpClient.get<any[]>(url))),
    );
  }

  // Get available field IDs that are not yet configured in Field Config
  getUnselectedFieldIds(): Observable<any[]> {
    const url = `${this.apiUrl}/fields-config/unselected-field-ids`;
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() => this.withTimeout(this.httpClient.get<any[]>(url))),
    );
  }

  // Create Field Config row
  createFieldsConfig(payload: {
    uniqueid: number;
    allowed_emails: string[];
    description?: string;
  }): Observable<any> {
    const url = `${this.apiUrl}/fields-config`;
    return this.withTimeout(this.httpClient.post(url, payload));
  }

  // Update Field Config row
  updateFieldsConfig(
    uniqueid: number,
    payload: { allowed_emails: string[]; description?: string },
  ): Observable<any> {
    const url = `${this.apiUrl}/fields-config/${uniqueid}`;
    return this.withTimeout(this.httpClient.patch(url, payload));
  }

  // Delete Field Config row
  deleteFieldsConfig(uniqueid: number): Observable<any> {
    const url = `${this.apiUrl}/fields-config/${uniqueid}`;
    return this.withTimeout(this.httpClient.delete(url));
  }

  /**
   * IM-613: Fetch a single field as a GeoJSON feature (same shape as a feature in
   * GET /fields) so the caller can patch just that field in place instead of
   * re-downloading the whole client collection.
   */
  getFieldFeature(
    fieldId: number | string,
    clientId?: string,
    includeEt = true,
    includeEtDate?: string,
  ): Observable<any> {
    const url = `${this.apiUrl}/fields/${fieldId}/feature`;
    let params = new HttpParams();
    if (clientId) {
      params = params.set("clientId", clientId);
    }
    params = params.set("includeEt", String(includeEt));
    if (includeEtDate) {
      params = params.set("includeEtDate", includeEtDate);
    }
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() => this.withTimeout(this.httpClient.get(url, { params }))),
    );
  }

  getFieldById(fieldId: string): Observable<any> {
    const url = `${this.apiUrl}/fieldsnoet/${fieldId}`;
    if (fieldId) {
      let params = new HttpParams().set("fieldid", fieldId);
      return this.refreshTrigger$.pipe(
        take(1),
        switchMap(() =>
          this.withTimeout(
            this.httpClient.get<FieldFeatureCollection>(url, { params }),
          ),
        ),
      );
    } else {
      throw new Error("Not set the fieldId");
    }
  }

  /**
   * Getting all claimed fields with et-values of the current login user
   * @param userId
   */
  getClaimedFields(userId: string, includeEt = false) {
    const url = `${this.apiUrl}/claimed-fields`;
    const params = new HttpParams().set("includeEt", String(includeEt));
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() =>
        this.withTimeout(this.httpClient.get<ClaimedField[]>(url, { params })),
      ),
    );
  }

  getClaimedFieldsWithEt(userId: string) {
    return this.getClaimedFields(userId, true);
  }

  getFieldWeather(
    lat: number,
    long: number,
    stationId: string,
    stationIdForecast: string,
  ) {
    const url = `${this.apiUrl}/field-weather/${lat}/${long}/${stationId}/${stationIdForecast}`;
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() => this.withTimeout(this.httpClient.get<any>(url))),
    );
  }

  getClaimedFieldEtValues(userFieldId: number): Observable<UserFieldEtValue[]> {
    const url = `${this.apiUrl}/claimed-fields/${userFieldId}/et-values`;
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() =>
        this.withTimeout(this.httpClient.get<UserFieldEtValue[]>(url)),
      ),
    );
  }

  getClaimedFieldIrrigatedInfo(
    claimedFieldId: number,
  ): Observable<ClaimedFieldIrrigatedInfoResponse> {
    const url = `${this.apiUrl}/claimed-fields/${claimedFieldId}/irrigated-info`;
    return this.withTimeout(
      this.httpClient.get<ClaimedFieldIrrigatedInfoResponse>(url),
    );
  }

  saveClaimedFieldIrrigatedInfo(
    claimedFieldId: number,
    irrigatedInfo: IrrigatedInfo,
  ): Observable<ClaimedFieldIrrigatedInfoResponse> {
    const url = `${this.apiUrl}/claimed-fields/${claimedFieldId}/irrigated-info`;
    return this.withTimeout(
      this.httpClient.post<ClaimedFieldIrrigatedInfoResponse>(url, {
        irrigated_info: irrigatedInfo,
      }),
    );
  }

  previewAppliedWaterCalculation(
    claimedFieldId: number,
    payload: AppliedWaterCalculationPayload,
  ): Observable<AppliedWaterCalculationResponse> {
    const url = `${this.apiUrl}/claimed-fields/${claimedFieldId}/applied-water-estimate`;
    return this.withTimeout(
      this.httpClient.post<AppliedWaterCalculationResponse>(url, payload),
    );
  }

  getAdminStatisticsSummary() {
    const url = `${this.apiUrl}/admin-statistics-summary`;
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() => this.withTimeout(this.httpClient.get<AdminData>(url))),
    );
  }

  getManagerStatisticsSummary(): Observable<any> {
    const url = `${this.apiUrl}/manager-statistics`;
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() => this.withTimeout(this.httpClient.get<any>(url))),
    );
  }

  /**
   * Getting all claimed fields with et-values of the current login user (Manager)
   * @param userId
   */
  getClaimedFieldsManager(includeEt = false): Observable<any> {
    const url = `${this.apiUrl}/claimed-fields-manager`;
    const params = new HttpParams().set("includeEt", String(includeEt));
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() =>
        this.withTimeout(this.httpClient.get<any>(url, { params })),
      ),
    );
  }

  getClaimedFieldsManagerWithEt(): Observable<any> {
    return this.getClaimedFieldsManager(true);
  }

  getClaimedFieldsEntity(includeEt = false): Observable<any> {
    const url = `${this.apiUrl}/claimed-fields-entity`;
    const params = new HttpParams().set("includeEt", String(includeEt));
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() =>
        this.withTimeout(this.httpClient.get<any>(url, { params })),
      ),
    );
  }

  getClaimedFieldsEntityWithEt(): Observable<any> {
    return this.getClaimedFieldsEntity(true);
  }
  // Pre-flight check used by the field-claim modal before adding a new
  // entity. Returns `{ valid: false, reason }` if the email belongs to an
  // existing grower or manager (entities can only be third-party users).
  checkEntityEmail(
    email: string,
  ): Observable<{ valid: boolean; reason?: string }> {
    const url = `${this.apiUrl}/users/check-entity-email?email=${encodeURIComponent(email)}`;
    return this.withTimeout(
      this.httpClient.get<{ valid: boolean; reason?: string }>(url),
    );
  }

  // Get unique entity name/email pairs for a grower. Managers may pass
  // `targetUserId` to fetch entities for a grower they manage.
  getGrowerEntities(
    targetUserId?: string,
  ): Observable<{ name: string; email: string }[]> {
    const base = `${this.apiUrl}/grower-entities`;
    const url =
      targetUserId ?
        `${base}?target_user_id=${encodeURIComponent(targetUserId)}`
      : base;
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() =>
        this.withTimeout(
          this.httpClient.get<{ name: string; email: string }[]>(url),
        ),
      ),
    );
  }

  checkFieldClaimStatus(fieldId: number): Observable<{
    emailValidation: boolean;
    isClaimed: boolean;
    isClaimedByCurrentUser: boolean;
    message: string;
  }> {
    const url = `${this.apiUrl}/fields/${fieldId}/claim-status`;
    return this.withTimeout(
      this.httpClient.get<{
        emailValidation: boolean;
        isClaimed: boolean;
        isClaimedByCurrentUser: boolean;
        message: string;
      }>(url),
    );
  }

  getPreclaimedInfo(fieldId: number): Observable<PreclaimedInfoResponse> {
    const url = `${this.apiUrl}/fields/${fieldId}/preclaimed`;
    return this.withTimeout(this.httpClient.get<PreclaimedInfoResponse>(url));
  }

  // Create or update field with option to claim as a specific user (for managers/admins)
  createOrUpdateFields(
    fieldId: number,
    createOrUpdateDto: FieldFormData,
    claimAsUserId?: string,
  ) {
    const url = `${this.apiUrl}/fields/${fieldId}`;
    const headers = new HttpHeaders({ "Content-Type": "application/json" });
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() =>
        this.withTimeout(
          this.httpClient.put(url, createOrUpdateDto, {
            headers,
            params:
              claimAsUserId ? { target_user_id: claimAsUserId } : undefined,
          }),
        ),
      ),
    );
  }

  deleteFields(fieldId: number) {
    const url = `${this.apiUrl}/fields/${fieldId}`;
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() => this.withTimeout(this.httpClient.delete(url))),
    );
  }

  // Retrieve the complete list of users for the admin dashboard table
  getUsers(): Observable<any[]> {
    const url = `${this.apiUrl}/admin/grower-users`; // Changed endpoint
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() => this.withTimeout(this.httpClient.get<any[]>(url))),
    );
  }

  // Retreive the list of users that can be assigned to a field
  getGrowerList(): Observable<GrowerListResponse> {
    const url = `${this.apiUrl}/growers`;
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() =>
        this.withTimeout(this.httpClient.get<GrowerListResponse>(url)),
      ),
    );
  }

  // Persist edits to a specific user from the admin management panel
  updateUser(userId: string, userData: any): Observable<any> {
    const url = `${this.apiUrl}/admin/users/${userId}`;
    return this.withTimeout(this.httpClient.patch(url, userData));
  }

  // Remove a given user record via the admin interface
  deleteUser(userId: string): Observable<any> {
    const url = `${this.apiUrl}/admin/users/${userId}`;
    return this.withTimeout(this.httpClient.delete(url));
  }

  // List grower invitations for the current manager/admin
  getUserInvitations(): Observable<any[]> {
    const url = `${this.apiUrl}/admin/users/invitations`;
    return this.refreshTrigger$.pipe(
      take(1),
      switchMap(() => this.withTimeout(this.httpClient.get<any[]>(url))),
    );
  }

  // Delete a specific invitation
  deleteUserInvitation(invitationId: string): Observable<any> {
    const url = `${this.apiUrl}/admin/users/invitations/${invitationId}`;
    return this.withTimeout(this.httpClient.delete(url));
  }

  // Send a user invitation email through the admin dashboard.
  // Pass force=true ONLY for explicit Resend — it bypasses the "already
  // pending invitation" backend guard and refreshes the existing row.
  sendUserInvitation(
    name: string,
    email: string,
    role?: "GROWER" | "MANAGER" | "ADMIN",
    clientId?: number,
    force?: boolean,
  ): Observable<any> {
    const url = `${this.apiUrl}/admin/users/invitations`;
    const body: {
      name: string;
      email: string;
      role?: string;
      clientId?: number;
      force?: boolean;
    } = { name, email };
    if (role) {
      body.role = role;
    }
    if (clientId) {
      body.clientId = clientId;
    }
    if (force) {
      body.force = true;
    }
    return this.withTimeout(this.httpClient.post(url, body));
  }

  // Resend an Entity invitation — uses the dedicated backend entity flow
  // so the recipient receives the correct "invited as Entity" email with
  // the original grower's name preserved.
  resendEntityInvitation(
    name: string,
    email: string,
    growerUserId: string,
    clientId?: number,
  ): Observable<any> {
    const url = `${this.apiUrl}/admin/users/invitations/entity-resend`;
    const body: {
      name: string;
      email: string;
      growerUserId: string;
      clientId?: number;
    } = { name, email, growerUserId };
    if (clientId) {
      body.clientId = clientId;
    }
    return this.withTimeout(this.httpClient.post(url, body));
  }

  // Send multiple user invitations imported from CSV
  // clientId applies to all invitations in the batch
  bulkSendUserInvitations(
    invitations: Array<{ name: string; email: string }>,
    role?: "GROWER" | "MANAGER",
    clientId?: number,
  ): Observable<any> {
    const url = `${this.apiUrl}/admin/users/invitations/bulk`;
    const body: {
      invitations: Array<{ name: string; email: string }>;
      role?: string;
      clientId?: number;
    } = { invitations };
    if (role) {
      body.role = role;
    }
    if (clientId) {
      body.clientId = clientId;
    }
    // No timeout for bulk invitations — the request may take several minutes
    // for large CSV files. The UI spinner stays active until the backend responds.
    return this.httpClient.post(url, body);
  }
}
